import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { newsletterApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../ui/accordion';
import { Mail, Send } from 'lucide-react';
import AutoxiumLogo from '../AutoxiumLogo';

const footerColumns = [
  {
    title: 'Explore',
    links: [
      { label: 'Home', to: '/' },
      { label: 'Articles', to: '/articles' },
      { label: 'How Does It Work?', to: '/how-it-works' },
      { label: 'Simulation', to: '/simulation' },
      { label: 'Tools', to: '/tools' },
      { label: 'Courses', to: '/courses', soon: true },
      { label: 'Community', to: '/community' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Glossary', to: '/glossary' },
      { label: "Beginner's Guide", to: '/beginners-guide' },
      { label: 'Request a Topic', to: '/request-topic' },
    ],
  },
  {
    title: 'Community',
    links: [
      { label: 'Q&A Discussions', to: '/community' },
      { label: 'Content Guidelines', to: '/content-guidelines' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Contact', to: '/contact' },
      { label: 'Careers', to: '/careers' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', to: '/privacy-policy' },
      { label: 'Terms of Service', to: '/terms-of-service' },
      { label: 'Cookie Policy', to: '/cookie-policy' },
      { label: 'Accessibility Statement', to: '/accessibility' },
    ],
  },
];

// Shared focus-visible treatment for interactive elements on the dark footer -
// same accent (autoxium-blue) as the site-wide `ring-ring` focus style, tuned for
// contrast against the chassis background.
const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-autoxium-blue focus-visible:ring-offset-2 focus-visible:ring-offset-chassis';

const FooterLink = ({ to, children, soon }) => (
  <Link
    to={to}
    className={`inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors rounded-sm ${focusRing}`}
  >
    {children}
    {soon && (
      <span className="text-[8px] font-mono bg-autoxium-blue px-1 py-0.5 rounded uppercase text-white leading-none">
        Soon
      </span>
    )}
  </Link>
);

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || subscribing) return;
    setSubscribing(true);
    try {
      await newsletterApi.subscribe(email.trim());
      setSubscribed(true);
      toast.success("You're on the list — thanks for subscribing.");
      setEmail('');
    } catch (err) {
      console.error('Error subscribing to newsletter:', err);
      toast.error(err.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-chassis text-white border-t border-white/10" data-testid="site-footer">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-12 gap-12">
          {/* Brand column */}
          <div className="md:col-span-4 lg:col-span-3">
            <Link to="/" className={`inline-flex items-center rounded-sm ${focusRing}`}>
              <AutoxiumLogo variant="light" width={70} height={28} />
            </Link>
            <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mt-4 mb-3">
              Automotive Knowledge Platform
            </p>
            <p className="text-sm text-white/60 leading-relaxed max-w-xs mb-6">
              Technical articles, tools, and a community built for automotive
              engineers who want to go deeper than the datasheet.
            </p>

            {/* Newsletter signup */}
            <form onSubmit={handleSubscribe} className="mb-6" data-testid="newsletter-form">
              <label
                htmlFor="footer-newsletter-email"
                className="font-chivo text-xs font-bold uppercase tracking-wider text-white block mb-2"
              >
                Stay Updated
              </label>
              {subscribed ? (
                <p className="text-sm text-autoxium-blue" data-testid="newsletter-success">
                  You're on the list — thanks for subscribing.
                </p>
              ) : (
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
                    <Input
                      id="footer-newsletter-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className={`pl-9 bg-white/5 border-white/15 text-white placeholder:text-white/40 ${focusRing}`}
                      data-testid="newsletter-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="icon"
                    disabled={subscribing}
                    className={`bg-autoxium-blue hover:bg-autoxium-blue/90 text-white shrink-0 ${focusRing}`}
                    data-testid="newsletter-submit-btn"
                    aria-label="Subscribe"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </form>

            {/* Status indicator */}
            <div className="flex items-center gap-2" data-testid="status-indicator">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-autoxium-blue opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-autoxium-blue" />
              </span>
              <span className="font-mono text-xs uppercase tracking-wider text-white/60">
                All systems operational
              </span>
            </div>
          </div>

          {/* Link columns - desktop */}
          <div className="hidden md:grid md:col-span-8 lg:col-span-9 grid-cols-2 lg:grid-cols-5 gap-8">
            {footerColumns.map((col) => (
              <div key={col.title}>
                <h3 className="font-chivo text-sm font-bold uppercase tracking-wider text-white mb-4">
                  {col.title}
                </h3>
                <ul className="space-y-3">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <FooterLink to={link.to} soon={link.soon}>{link.label}</FooterLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Link columns - accordion below tablet width */}
          <div className="md:hidden">
            <Accordion type="single" collapsible className="w-full">
              {footerColumns.map((col) => (
                <AccordionItem key={col.title} value={col.title} className="border-white/10">
                  <AccordionTrigger
                    className={`font-chivo text-sm font-bold uppercase tracking-wider text-white hover:no-underline ${focusRing}`}
                  >
                    {col.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-3 pb-2">
                      {col.links.map((link) => (
                        <li key={link.label}>
                          <FooterLink to={link.to} soon={link.soon}>{link.label}</FooterLink>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-6 flex items-center justify-center sm:justify-start">
          <p className="text-xs text-white/50 text-center sm:text-left">
            © {new Date().getFullYear()} AUTOXIUM. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
